import { encounterField, encounterCreatureField, sizeIdOf, posCoord } from '../../shared/array-types';
import { isEncounterIdentity } from '../encounter-identity';
import { plTotal } from '../pl/xref';
import { createFormulaReadContext } from './formula-read-context';
import { readItemField } from './item-field-reader';
import { readInventoryField, readInventoryRows, readLayerListEntryUuid } from './inventory-raw-readers';
import { phpJsonInt, phpJsonTruthy } from './php-json-values';
import { phpJsonFloat } from './php-decimal-values';
import { assertUncomputedFromBase, withFromBaseValidation, fromBaseRecord, fromBaseUuid, fromBaseClone, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import { yieldBetweenMemberCompiles } from './compile-task';
import type { FromBaseRow as Row } from './from-base-context';
import { constructPlayerThroughIntegersFromBase } from './from-base-player-integers';
import type { FromBasePlayerIntegerMetadata } from './from-base-player-integers';
import { constructEncounterMemberThroughCapacityFromBase } from './from-base-encounter-members';
import type { FromBasePlayerFeatureMetadata } from './from-base-player-features';
import { compileInventoryTreeFromBase } from './from-base-inventory';
import type { FromBaseInventoryMetadata, FromBaseInventoryTree } from './from-base-inventory';
import { compileEncounterShellFromBase } from './from-base-encounter-shell';
import type { EncounterShellSource, FromBaseEncounterShellMetadata } from './from-base-encounter-shell';
import { compileEncounterTerrainFromBase } from './from-base-encounter-terrain';
import type { EncounterTerrainMetadata } from './from-base-encounter-terrain';
import { compileEncounterExplorationFromBase } from './from-base-encounter-exploration';
import { compileEncounterZoneKeysFromBase } from './from-base-encounter-zones';
import { compileEncounterDefinitionSurface } from './from-base-encounter-status';
import { mergeFromBaseCatalogEntry, normalizeFromBaseCatalog } from './from-base-catalog-normalization';
import type { FromBaseCatalogContext } from './from-base-catalog-normalization';
import { createInventoryTemplateIdentityResolver } from './inventory-template-identity';
import { encounterMemberInput } from './encounter-member-input';

export const FROM_BASE_ENCOUNTER_REMAINING_STAGES = [
    'encounter-members', 'encounter-floor-inventory', 'encounter-status-definition-finalization',
    'terrain-zone-definition-finalization', 'encounter-catalog-lean-normalization',
] as const;

export interface FromBaseEncounter extends EncounterShellSource, FromBaseInventoryTree {
    encounter_creatures_arr: readonly Row[];
    campaign_arr?: Row;
    media_context?: readonly Row[];
    floor_order?: readonly string[];
    roster_order?: readonly string[];
    encounter_exploration_states?: readonly Row[];
    random_table_rows?: FromBaseCatalogContext['randomTableRows'];
    /** Immutable frontend registries; never a compiled prior or viewer permissions. */
    encounter_metadata?: FromBaseEncounterShellMetadata & EncounterTerrainMetadata & {
        memberCompiler?: (base: Row) => { computed: unknown; stagesRun: string[]; stagesMissing: string[]; stageDiagnostics?: readonly string[] };
        player?: FromBasePlayerIntegerMetadata & Partial<Pick<FromBasePlayerFeatureMetadata, 'features'>>;
        inventory?: FromBaseInventoryMetadata;
    };
}
export interface FromBaseEncounterResult {
    kind: 'constructed-encounter' | 'missing-stage';
    /** Encounter-own producers complete; refused children retain repairable saved identities. */
    row: Row;
    /** Raw status-layer checkpoint; definition display/PL/full nesting remain unfinished. */
    statusProjection?: Row;
    /** Reference-shell checkpoint, separate from the raw status payload and runtime catalog. */
    statusDefinitionProjections?: Readonly<Record<string, Row>>;
    /** Definition presentation checkpoint; classification, full nests and PL remain unfinished. */
    statusPresentationProjections?: Readonly<Record<string, Row>>;
    memberProjections: readonly { entity_id: string; base_rev: number;
    result: ReturnType<typeof constructPlayerThroughIntegersFromBase> | ReturnType<typeof constructEncounterMemberThroughCapacityFromBase>
        | { kind: 'constructed-prefix'; row: Row; completedStages: string[]; missingStages: string[] } | null }[];
    inventoryProjection?: ReturnType<typeof compileInventoryTreeFromBase>;
    diagnostics?: readonly string[];
    completedStages: readonly string[];
    missingStages: readonly string[];
    /** C1's creature-only refusal families remain unchanged by this encounter checkpoint. */
    closedRefusalFamilies: readonly string[];
}

/** Native get_encounter_creatures_for_encounter_arr totals over completed members.
 * A missing price is a disclosed partial total; an unfinished member is a construction gap upstream. */
export function compileEncounterRosterTotals(members: readonly Row[], read: (row: Row, name: string) => unknown): Row {
    let cr = 0, pl = 0, unpriced = 0;
    for (const member of members) {
        const value = read(member, 'cr');
        if (phpJsonTruthy(value)) cr += phpJsonFloat(value);
        const price = plTotal(member);
        if (price !== null) pl += phpJsonInt(price); else unpriced++;
    }
    return { total_cr: cr, total_pl: pl, total_pl_partial: unpriced > 0, total_pl_unpriced: unpriced };
}

/** Select only the inventory producer's saved inputs; encounter checkpoints are separate producers. */
export function encounterInventoryInputFromBase(base: FromBaseEncounter, metadata: NonNullable<FromBaseEncounter['encounter_metadata']>): FromBaseInventoryTree {
    const inventory = metadata.inventory;
    if (!inventory) throw new FromBaseStageMissing('floor-frontend-metadata', base.entity_id);
    return { entity_id: base.entity_id, base_rev: base.base_rev,
            inventory: base.inventory, enchantment_templates: base.enchantment_templates,
            enchantment_template_enchantments: base.enchantment_template_enchantments,
            missing_definition_ids: base.missing_definition_ids, pl_signature: base.pl_signature,
            encounter_links: base.encounter_links, inventory_order: base.inventory_order,
            inventory_metadata: base.inventory_metadata, definition_videos: base.definition_videos,
            metadata: { ...inventory, environmentDefinition: metadata.definitionLookup },
            picture_sources: base.media_context, picture_lookup_ids: base.media_context?.map(row => String(readItemField(row, 'picture_id'))),
            possessor: { encounters: base.encounters } };
}

/** C0 composition keeps child refusals local; encounter-own gaps still refuse the encounter. */
export function compileEncounterFromBase(base: FromBaseEncounter, catalog: Readonly<Record<string, Row>> | readonly []): FromBaseEncounterResult {
    const work = constructEncounterFromBase(base, catalog);
    return withFromBaseValidation(() => {
        let step = work.next();
        while (!step.done) step = work.next();
        return step.value;
    });
}

export async function compileEncounterFromBaseAsync(base: FromBaseEncounter, catalog: Readonly<Record<string, Row>> | readonly [],
    seen: WeakSet<object>, current: () => boolean = () => true, progress: () => void = () => {}): Promise<FromBaseEncounterResult> {
    const work = constructEncounterFromBase(base, catalog);
    let step = withFromBaseValidation(() => work.next(), seen);
    while (!step.done) {
        progress();
        await yieldBetweenMemberCompiles();
        if (!current()) throw new Error('compile-worker-superseded');
        step = withFromBaseValidation(() => work.next(), seen);
    }
    return step.value;
}

function* constructEncounterFromBase(base: FromBaseEncounter, catalog: Readonly<Record<string, Row>> | readonly []): Generator<void, FromBaseEncounterResult> {
    assertUncomputedFromBase(base); assertUncomputedFromBase(catalog, 'catalog');
    if (!isEncounterIdentity(base.entity_id)
        || !Number.isSafeInteger(base.base_rev) || base.base_rev < 0
        || !fromBaseRecord(base.encounters) || encounterField(base, 'encounter_id')?.toUpperCase() !== base.entity_id.toUpperCase()
        || !Array.isArray(base.encounter_creatures_arr) || !Array.isArray(base.inventory)
        || !base.statuses || !Array.isArray(base.statuses.target) || !Array.isArray(base.statuses.caster)
        || !(fromBaseRecord(catalog) || Array.isArray(catalog) && catalog.length === 0)) throw new TypeError('Invalid raw encounter envelope');
    const definitions: Record<string, Row> = {};
    for (const [id, definition] of Object.entries(catalog)) {
        if (!fromBaseUuid(id) || !fromBaseRecord(definition) || !fromBaseRecord(definition.items)
            || !(fromBaseRecord(definition.item_layer_types) || Array.isArray(definition.item_layer_types) && definition.item_layer_types.length === 0)
            || String(readItemField(definition, 'item_id')).toUpperCase() !== id.toUpperCase()) throw new TypeError(`Invalid encounter definition: ${id}`);
        if (definitions[id.toUpperCase()]) throw new TypeError(`Duplicate encounter definition: ${id}`);
        definitions[id.toUpperCase()] = definition;
    }
    for (const id of base.missing_definition_ids ?? []) {
        if (!fromBaseUuid(id) || definitions[id.toUpperCase()]) throw new TypeError(`Invalid encounter known miss: ${id}`);
    }
    const metadata = base.encounter_metadata;
    const shell = compileEncounterShellFromBase(base, definitions, metadata);
    const completed = [...shell.completedStages], missing = [...shell.missingStages], diagnostics: string[] = [];
    let row = shell.row;
    const memberProjections: FromBaseEncounterResult['memberProjections'][number][] = [];
    const rowRefusals: { entity_id: string; reason: string }[] = [];
    const refusedMembers = new Map<string, Row>(), refusedFloor = new Map<string, Row>(), refusedFloorIds = new Set<string>();
    const memberPlaceholder = (member: Row, reason: string): Row => {
        const sourceId = encounterCreatureField(member.encounter_creatures, 'item_id');
        // A failed later rule window does not erase the body's already-resolved ancestry size.
        const partial = memberProjections.find(projection => projection.entity_id === member.entity_id)?.result;
        const body = partial?.kind === 'constructed-prefix' ? partial.row : null;
        const size = sizeIdOf(body) ?? sizeIdOf(member.template) ?? sizeIdOf(definitions[String(sourceId).toUpperCase()]);
        return { encounter_creatures: fromBaseClone(member.encounter_creatures as Row),
            position: Object.fromEntries(['x_pos', 'y_pos', 'z_pos'].map(axis =>
                [axis, posCoord(member.encounter_creatures, axis as 'x_pos' | 'y_pos' | 'z_pos') ?? 0])),
            ...(fromBaseRecord(member.player_characters) ? { player_characters: fromBaseClone(member.player_characters) } : {}),
            ...(size ? { item_layer_types: { size } } : {}),
            ...(fromBaseRecord(body?.assets) ? { assets: fromBaseClone(body.assets) } : {}),
            items: { item_id: sourceId, item_type: encounterCreatureField(member.encounter_creatures, 'item_type'),
                item_name: encounterCreatureField(member.encounter_creatures, 'encounter_creature_name')
                    || readItemField(fromBaseRecord(member.player_characters) ? member.player_characters : null, 'player_name')
                    || readItemField(definitions[String(sourceId).toUpperCase()] ?? null, 'item_name') || 'Creature' },
            // Saved inputs are retained separately from the unusable compiled projection.
            // Time deferral must inspect these rows, never infer idleness from the placeholder.
            _time_source: { statuses: member.statuses, inventory: member.inventory },
            _entity_refusal: reason, _inventory_refusal: reason };
    };
    const floorPlaceholder = (node: typeof base.inventory[number], reason: string): Row => {
        const id = String(readInventoryField(node, 'inventory_item_id')).toUpperCase(), definition = definitions[id] ?? null;
        return { inventory: fromBaseClone(node.inventory), items: { item_id: id, item_type: readItemField(definition, 'item_type'),
            item_name: readInventoryField(node, 'inventory_name') || readItemField(definition, 'item_name') || 'Item' },
            _entity_refusal: reason, _inventory_refusal: reason };
    };
    const ids = new Set<string>();
    const members = new Map(base.encounter_creatures_arr.map(member => [String(member.entity_id).toUpperCase(), member]));
    if (members.size !== base.encounter_creatures_arr.length) throw new TypeError('Invalid encounter member identity: duplicate');
    if (base.roster_order && (base.roster_order.length !== Math.min(50, members.size)
        || new Set(base.roster_order.map(id => id.toUpperCase())).size !== base.roster_order.length
        || base.roster_order.some(id => !members.has(id.toUpperCase())))) throw new TypeError('Invalid encounter roster order');
    if (base.roster_order) completed.push('encounter-database-roster-order'); else missing.push('encounter-database-roster-order');
    if (base.floor_order || !base.inventory.length) completed.push('encounter-database-floor-order'); else missing.push('encounter-database-floor-order');
    let inventoryProjection: ReturnType<typeof compileInventoryTreeFromBase> | undefined;
    let compiledFloor: Row[] | undefined;
    const floorDefinitions: Record<string, Row> = {};
    if (!base.inventory.length) {
        row.encounter_inventories_arr = []; completed.push('encounter-floor-inventory');
    } else if (metadata?.inventory) {
        const inventoryInput = encounterInventoryInputFromBase(base, metadata);
        inventoryProjection = compileInventoryTreeFromBase(inventoryInput, definitions);
        if (inventoryProjection.inventory && !inventoryProjection.missingStages.length) {
            row.encounter_inventories_arr = compiledFloor = inventoryProjection.inventory;
            Object.assign(floorDefinitions, inventoryProjection.definitions);
        } else {
            // Only a failing floor retries by root. A container's subtree is one repairable row.
            const children = new Map<string, typeof base.inventory[number][]>();
            for (const node of base.inventory) {
                const parent = String(readInventoryField(node, 'possess_id')).toUpperCase();
                const list = children.get(parent) ?? []; list.push(node); children.set(parent, list);
            }
            const collect = (node: typeof base.inventory[number]): typeof base.inventory[number][] =>
                [node, ...(children.get(String(readInventoryField(node, 'inventory_id')).toUpperCase()) ?? []).flatMap(collect)];
            const floor: Row[] = [];
            compiledFloor = floor;
            for (const node of children.get(base.entity_id.toUpperCase()) ?? []) {
                const nodes = collect(node), nodeIds = new Set(nodes.map(child => readInventoryField(child, 'inventory_id')));
                const result = compileInventoryTreeFromBase({ ...inventoryInput, inventory: nodes,
                    inventory_order: base.inventory_order?.filter(child => nodeIds.has(readInventoryField({ inventory: child }, 'inventory_id'))),
                }, definitions);
                if (result.inventory && !result.missingStages.length) {
                    floor.push(...result.inventory);
                    Object.assign(floorDefinitions, result.definitions);
                } else {
                    const id = String(readInventoryField(node, 'inventory_id')).toUpperCase();
                    const reason = `unported:floor-row:${result.missingStages[0]}${result.detail ? `:${result.detail}` : ''}`;
                    refusedFloor.set(id, floorPlaceholder(node, reason));
                    for (const child of nodes) refusedFloorIds.add(String(readInventoryField(child, 'inventory_id')).toUpperCase());
                    rowRefusals.push({ entity_id: id, reason });
                }
            }
            row.encounter_inventories_arr = floor;
        }
        completed.push('encounter-floor-inventory');
    } else {
        row.encounter_inventories_arr = [];
        for (const node of base.inventory) if (String(readInventoryField(node, 'possess_id')).toUpperCase() === base.entity_id.toUpperCase()) {
            const id = String(readInventoryField(node, 'inventory_id')).toUpperCase(), reason = 'unported:floor-row:frontend-metadata';
            refusedFloor.set(id, floorPlaceholder(node, reason));
            rowRefusals.push({ entity_id: id, reason });
        }
        completed.push('encounter-floor-inventory');
        for (const node of base.inventory) refusedFloorIds.add(String(readInventoryField(node, 'inventory_id')).toUpperCase());
    }
    for (const member of base.roster_order ? base.roster_order.map(id => members.get(id.toUpperCase())!) : base.encounter_creatures_arr) {
        const id = member.entity_id;
        if (!fromBaseUuid(id) || ids.has(id.toUpperCase()) || !Number.isSafeInteger(member.base_rev) || Number(member.base_rev) < 0
            || encounterCreatureField(member.encounter_creatures, 'encounter_creature_id')?.toUpperCase() !== id.toUpperCase()
            || encounterCreatureField(member.encounter_creatures, 'encounter_id')?.toUpperCase() !== base.entity_id.toUpperCase())
            throw new TypeError('Invalid encounter member identity/membership/revision');
        ids.add(id.toUpperCase());
        const memberMissing: string[] = [];
        try {
        if (metadata?.memberCompiler) {
            const compiled = metadata.memberCompiler({ ...member, catalog: definitions,
                encounter_context: member.encounter_context,
                missing_definition_ids: [...new Set([...(base.missing_definition_ids ?? []),
                    ...(Array.isArray(member.missing_definition_ids) ? member.missing_definition_ids as string[] : [])])] });
            if (!compiled.stagesMissing.length) diagnostics.push(...compiled.stageDiagnostics ?? []);
            const result = { kind: 'constructed-prefix' as const, row: compiled.computed as Row,
                completedStages: compiled.stagesRun, missingStages: compiled.stagesMissing };
            memberProjections.push({ entity_id: id, base_rev: Number(member.base_rev), result });
            if (compiled.stagesMissing.length) memberMissing.push(`member:${id}:${compiled.stageDiagnostics?.[0] ?? compiled.stagesMissing[0]}`);
        } else if (fromBaseRecord(member.player_characters) && metadata?.player) {
            const input = { ...encounterMemberInput(base as unknown as Row, member), catalog: definitions,
                missing_definition_ids: [...new Set([...(base.missing_definition_ids ?? []),
                    ...(Array.isArray(member.missing_definition_ids) ? member.missing_definition_ids as string[] : [])])] } as unknown as Parameters<typeof constructPlayerThroughIntegersFromBase>[0];
            const result = metadata.player.features
                ? constructEncounterMemberThroughCapacityFromBase(input, { ...metadata.player, features: metadata.player.features })
                : constructPlayerThroughIntegersFromBase(input, metadata.player);
            memberProjections.push({ entity_id: id, base_rev: Number(member.base_rev), result });
            if (result.kind === 'constructed-prefix') memberMissing.push(...result.missingStages.map(stage => `member:${id}:${stage}`));
            else memberMissing.push(`member:${id}:${result.kind}:${result.stage}`);
        } else {
            memberProjections.push({ entity_id: id, base_rev: Number(member.base_rev), result: null });
            memberMissing.push(`member:${id}:${fromBaseRecord(member.player_characters) ? 'player-frontend-metadata' : 'creature-from-base'}`);
        }
        } catch (error) {
            memberMissing.push(error instanceof Error ? error.message : String(error));
            memberProjections.push({ entity_id: id, base_rev: Number(member.base_rev), result: null });
        }
        const projection = memberProjections[memberProjections.length - 1]?.result;
        if (memberMissing.length || !projection || projection.kind !== 'constructed-prefix' || projection.missingStages.length || !fromBaseRecord(projection.row)) {
            const reason = `unported:encounter-member:${memberMissing.join(',') || 'compiler-output'}`;
            refusedMembers.set(id.toUpperCase(), memberPlaceholder(member, reason));
            rowRefusals.push({ entity_id: id, reason });
        }
        yield;
    }
    row.encounter_creatures_arr = memberProjections.flatMap(member =>
        !refusedMembers.has(member.entity_id.toUpperCase()) && member.result?.kind === 'constructed-prefix' ? [member.result.row] : []);
    const read = createFormulaReadContext({ catalogRows: metadata?.layerCatalog ?? {}, abilityTypes: metadata?.abilityTypes ?? [],
        aggregationStrategy: () => 'host_wins', resolveCarrierRef: metadata?.definitionLookup ?? (() => null), resolveInventoryInstance: () => null });
    const totals = compileEncounterRosterTotals(row.encounter_creatures_arr as Row[], read.layerValue);
    if (refusedMembers.size) { totals.total_pl_partial = true; totals.total_pl_unpriced = Number(totals.total_pl_unpriced) + refusedMembers.size; }
    Object.assign((row.computed as { encounter: Row }).encounter, totals);
    completed.push('encounter-members', 'encounter-roster-totals');

    const terrainRefusal = (id: string, detail: string, kind: 'member' | 'floor'): void => {
        id = id.toUpperCase();
        const reason = `unported:${kind === 'member' ? 'encounter-member' : 'floor-row'}:${detail}`;
        if (kind === 'member') {
            const member = members.get(id)!;
            refusedMembers.set(id, memberPlaceholder(member, reason));
        } else {
            const node = base.inventory.find(node => String(readInventoryField(node, 'inventory_id')).toUpperCase() === id)!;
            refusedFloor.set(id, floorPlaceholder(node, reason));
            refusedFloorIds.add(id);
        }
        rowRefusals.push({ entity_id: id, reason });
    };
    const refusedBeforeTerrain = refusedMembers.size;
    if (metadata) {
        const projectedIds = new Set(memberProjections.map(member => member.entity_id.toUpperCase()));
        const terrainBase = { ...base, encounter_creatures_arr: base.encounter_creatures_arr.filter(member => projectedIds.has(String(member.entity_id).toUpperCase()) && !refusedMembers.has(String(member.entity_id).toUpperCase())),
            roster_order: base.roster_order?.filter(id => !refusedMembers.has(id.toUpperCase())),
            floor_order: base.floor_order?.filter(id => !refusedFloorIds.has(id.toUpperCase())),
            inventory: base.inventory.filter(node => !refusedFloorIds.has(String(readInventoryField(node, 'inventory_id')).toUpperCase())) };
        try {
            const keys = compileEncounterZoneKeysFromBase(terrainBase, definitions, metadata, terrainRefusal);
            if (keys.length) row.difficult_terrain_zone_keys = keys;
            completed.push('terrain-zone-keys');
        } catch (error) {
            if (error instanceof FromBaseInputMissing || error instanceof FromBaseStageMissing) missing.push(error.message);
            else throw error;
        }
        const terrainMetadata = { ...metadata, definitionLookup: (id: string) => floorDefinitions[id.toUpperCase()]
            ?? metadata.definitionLookup?.(id) ?? definitions[id.toUpperCase()] ?? null };
        try { Object.assign(row, compileEncounterTerrainFromBase({ ...terrainBase, floor_order: terrainBase.floor_order?.filter(id => !refusedFloorIds.has(id.toUpperCase())),
            inventory: terrainBase.inventory.filter(node => !refusedFloorIds.has(String(readInventoryField(node, 'inventory_id')).toUpperCase())) }, definitions, terrainMetadata,
            compiledFloor?.filter(node => !refusedFloorIds.has(String(readInventoryField(node, 'inventory_id')).toUpperCase())),
            (id, reason) => terrainRefusal(id, reason, 'floor'))); completed.push('encounter-floor-terrain'); }
        catch (error) {
            if (error instanceof FromBaseInputMissing || error instanceof FromBaseStageMissing) missing.push(error.message);
            else throw error;
        }
    } else missing.push('encounter-terrain-metadata');
    try {
        row.encounter_exploration_states_arr = compileEncounterExplorationFromBase(base.entity_id, base.encounter_exploration_states);
        completed.push('encounter-exploration-overlay');
    } catch (error) {
        if (error instanceof FromBaseStageMissing) missing.push(error.message); else throw error;
    }
    row.encounter_creatures_arr = (row.encounter_creatures_arr as Row[]).filter(host => !refusedMembers.has(String(encounterCreatureField(host.encounter_creatures, 'encounter_creature_id')).toUpperCase()));
    row.encounter_inventories_arr = (row.encounter_inventories_arr as Row[]).filter(host => !refusedFloor.has(String(readInventoryField(host, 'inventory_id')).toUpperCase()));
    if (refusedMembers.size !== refusedBeforeTerrain) {
        const totals = compileEncounterRosterTotals(row.encounter_creatures_arr as Row[], read.layerValue);
        totals.total_pl_partial = true; totals.total_pl_unpriced = Number(totals.total_pl_unpriced) + refusedMembers.size;
        Object.assign((row.computed as { encounter: Row }).encounter, totals);
    }
    if (missing.length) missing.push('encounter-catalog-lean-normalization');
    else {
        try {
            row = compileEncounterCatalogFromBase(row, definitions, metadata, base.random_table_rows);
            completed.push('encounter-catalog-lean-normalization');
        } catch (error) {
            if (error instanceof FromBaseInputMissing || error instanceof FromBaseStageMissing) missing.push(error.message);
            else throw error;
        }
    }
    const restoreRows = (name: string, refused: Map<string, Row>, order: readonly string[] | undefined, field: (row: Row) => unknown): void => {
        const rows = new Map((row[name] as Row[] ?? []).map(host => [String(field(host)).toUpperCase(), host]));
        for (const [id, host] of refused) rows.set(id, host);
        row[name] = order ? order.map(id => rows.get(id.toUpperCase())).filter(Boolean) : [...rows.values()];
    };
    restoreRows('encounter_creatures_arr', refusedMembers, base.roster_order ?? base.encounter_creatures_arr.map(member => String(member.entity_id)),
        host => encounterCreatureField(host.encounter_creatures, 'encounter_creature_id'));
    // SELECT's floor order names direct roots. Resting items retain their own
    // rows even though their persisted possess link still names a holder.
    const floorOrder = base.floor_order && [...new Set([...base.floor_order.map(id => id.toUpperCase()),
        ...(row.encounter_inventories_arr as Row[]).map(host => String(readInventoryField(host, 'inventory_id')).toUpperCase())])];
    restoreRows('encounter_inventories_arr', refusedFloor, floorOrder, host => readInventoryField(host, 'inventory_id'));
    if (rowRefusals.length) {
        const order = new Map([...(base.floor_order ?? []), ...(base.roster_order ?? [])].map((id, index) => [id.toUpperCase(), index]));
        row._compile_row_refusals = rowRefusals.sort((a, b) => (order.get(a.entity_id.toUpperCase()) ?? Number.MAX_SAFE_INTEGER)
            - (order.get(b.entity_id.toUpperCase()) ?? Number.MAX_SAFE_INTEGER)
            || (a.entity_id < b.entity_id ? -1 : a.entity_id > b.entity_id ? 1 : a.reason < b.reason ? -1 : a.reason > b.reason ? 1 : 0));
    }
    return { kind: missing.length ? 'missing-stage' : 'constructed-encounter', row: fromBaseClone(row), statusProjection: shell.statusProjection,
        statusDefinitionProjections: shell.statusDefinitionProjections, statusPresentationProjections: shell.statusPresentationProjections,
        memberProjections, inventoryProjection, diagnostics: [...new Set(diagnostics)],
        completedStages: completed, missingStages: [...new Set(missing)], closedRefusalFamilies: [] };
}

/** Own shell interning and climate delivery after all member/floor producers finish.
 * An authored weather definition with layers still belongs to the definition producer. */
export function compileEncounterCatalogFromBase(source: Row, definitions: Readonly<Record<string, Row>>,
    metadata?: FromBaseEncounter['encounter_metadata'], randomTableRows?: FromBaseCatalogContext['randomTableRows'],
    heldCatalog?: FromBaseCatalogContext['heldCatalog']): Row {
    if (metadata?.definitionLookup) {
        const definition = metadata.definitionLookup;
        const readers = createFormulaReadContext({ catalogRows: metadata.layerCatalog, abilityTypes: metadata.abilityTypes,
            aggregationStrategy: name => metadata.layerCatalog[name]?.aggregation_strategy ?? 'host_wins', resolveCarrierRef: definition,
            resolveInventoryInstance: () => null, resolveInventoryIdentity: createInventoryTemplateIdentityResolver(definition) });
        let row = fromBaseClone(source);
        const weather = encounterField(row, 'encounter_weather');
        // Damage can split a run into fresh template instances. Keep canonical
        // templates and materials in the page scope, before instance thinning.
        const catalog = fromBaseRecord(row.catalog) ? row.catalog : {};
        const held = new Map<string, Row | null>();
        const hold = (value: unknown): Row | null => {
            if (!fromBaseUuid(value)) return null;
            const id = value.toUpperCase();
            if (held.has(id)) return held.get(id)!;
            const full = definition(id);
            held.set(id, full);
            if (full) mergeFromBaseCatalogEntry(catalog, id, full);
            return full;
        };
        const holdInventory = (nodes: Row[]): void => {
            for (const node of nodes) {
                const template = hold(readInventoryField(node, 'inventory_item_id'));
                if (template) for (const material of Object.values(readers.layerEntries(template, 'item_composition'))) hold(readLayerListEntryUuid(material));
                holdInventory(readInventoryRows(node, 'inventory_in_container'));
            }
        };
        holdInventory(readInventoryRows(row, 'encounter_inventories_arr'));
        if (Object.keys(catalog).length) row.catalog = catalog;
        row = normalizeFromBaseCatalog(row, { itemField: readers.itemField, definition,
            rawDefinition: id => definitions[id.toUpperCase()] ?? null, abilityTypes: metadata.abilityTypes, randomTableRows, heldCatalog });
        // Damage rolls also run inside attack listeners. Their layerless types
        // must remain definitions, rather than display-only catalog names.
        for (const [id, raw] of Object.entries(definitions)) {
            if (readItemField(raw, 'item_type') !== 'damage_type') continue;
            const full = definition(id);
            if (full) ((row.catalog ??= {}) as Row)[id.toUpperCase()] = full;
        }
        // Native climate delivery attaches the standalone weather AFTER interning.
        // Its catalog and gallery survive even when the authored layers are empty.
        if (fromBaseUuid(weather)) {
            const full = definition(weather);
            if (full) {
                const weatherDefinition = fromBaseClone(full);
                delete weatherDefinition.computed;
                if (fromBaseRecord(weatherDefinition.items)) { delete weatherDefinition.item_name; delete weatherDefinition.item_type; }
                const catalog = (row.catalog ??= {}) as Row;
                catalog[weather.toUpperCase()] = weatherDefinition;
            }
        }
        // Generic encounter delivery omits the all-team registry; perception serves the viewer slice.
        delete row.encounter_exploration_states_arr;
        (row.computed as Row)._cache = { actor_meshes_v: 2 };
        return row;
    }
    for (const name of ['encounter_creatures_arr', 'encounter_inventories_arr'])
        if (Object.keys(readItemField({ held: [source[name]] }, 'held') as Row ?? {}).length)
            throw new FromBaseStageMissing('encounter-catalog-lean-normalization', `unfinished-nested-catalog:${name}`);
    if (Object.keys(readItemField({ held: [source.statuses] }, 'held') as Row ?? {}).length)
        throw new FromBaseStageMissing('encounter-catalog-lean-normalization', 'status-definition-finalization');
    const row = fromBaseClone(source), catalog: Row = {};
    const bucket = row.encounters as Row;
    for (const name of ['encounter_location_type', 'encounter_environment', 'encounter_weather',
        'encounter_tempurature', 'encounter_wind', 'encounter_light']) {
        const id = encounterField(row, name), shell = readItemField({ held: [bucket[name]] }, 'held');
        if (!fromBaseUuid(id) || !fromBaseRecord(shell)) continue;
        const type = readItemField(shell, 'item_type'), itemName = readItemField(shell, 'item_name');
        catalog[id] = type === 'light_type' || type === 'weather_type' ? { item_name: itemName }
            : { item_name: itemName, item_type: type };
        bucket[name] = id;
    }
    const weather = encounterField(row, 'encounter_weather');
    if (fromBaseUuid(weather)) {
        const raw = definitions[weather.toUpperCase()];
        if (!raw) throw new FromBaseInputMissing('weather-definition', weather);
        if (Object.keys(raw.item_layer_types as Row).length)
            throw new FromBaseStageMissing('encounter-catalog-lean-normalization', `weather-definition-finalization:${weather}`);
        const definition = compileEncounterDefinitionSurface(raw, metadata?.cdnUrl);
        // Layerless standalone definitions carry their own interned identity.
        definition.catalog = { [weather]: { item_name: readItemField(raw, 'item_name') } };
        catalog[weather.toUpperCase()] = definition;
    }
    if (Object.keys(catalog).length) row.catalog = Object.fromEntries(Object.keys(catalog).sort().map(id => [id, catalog[id]]));
    const exploration = readItemField({ held: [row.encounter_exploration_states_arr] }, 'held');
    if (Array.isArray(exploration) && !exploration.length) delete row.encounter_exploration_states_arr;
    // The native marker prevents a redundant read-time repair. Empty rosters need no actor bake.
    (row.computed as Row)._cache = { actor_meshes_v: 2 };
    return row;
}
